// src/pages/usermanagement/UserForm.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { 
  Save, 
  X, 
  Eye, 
  EyeOff,
  AlertCircle,
  CheckCircle,
  Loader,
  ArrowLeft
} from 'lucide-react';

// Services
import userService from '../../services/userService';

// Auth
import { HasPermission } from '../../components/auth';
import { 
  PERMISSIONS, 
  USER_ROLES, 
  USER_STATUS,
  ROLE_LABELS,
  STATUS_LABELS,
  VALIDATION,
  isFieldRole
} from '../../components/auth/constants';

const UserForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  // State
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    phone: '',
    role: USER_ROLES.SALES,
    status: USER_STATUS.ACTIVE,
    canAccessWeb: true,
    canAccessMobile: false,
  });

  // Validation errors
  const [errors, setErrors] = useState({});

  // Validation state
  const [validating, setValidating] = useState({
    username: false,
    email: false,
  });

  // Load user data if edit mode
  useEffect(() => {
    if (isEditMode) {
      loadUser();
    }
  }, [id]);

  // Auto-set mobile access based on role
  useEffect(() => {
    if (isFieldRole(formData.role)) {
      setFormData(prev => ({
        ...prev,
        canAccessMobile: true
      }));
    }
  }, [formData.role]);

  const loadUser = async () => {
    try {
      setLoading(true);
      const user = await userService.getUserById(id);
      setFormData({
        username: user.username || '',
        email: user.email || '',
        password: '', // Don't load password
        confirmPassword: '',
        fullName: user.fullName || '',
        phone: user.phone || '',
        role: user.role || USER_ROLES.SALES,
        status: user.status || USER_STATUS.ACTIVE,
        canAccessWeb: user.canAccessWeb ?? true,
        canAccessMobile: user.canAccessMobile ?? false,
      });
    } catch (error) {
      toast.error('Failed to load user data');
      console.error(error);
      navigate('/usermanagement');
    } finally {
      setLoading(false);
    }
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate field
  const validateField = (name, value) => {
    switch (name) {
      case 'username':
        if (!value) return 'Username is required';
        if (value.length < VALIDATION.USERNAME.MIN_LENGTH) {
          return `Username must be at least ${VALIDATION.USERNAME.MIN_LENGTH} characters`;
        }
        if (value.length > VALIDATION.USERNAME.MAX_LENGTH) {
          return `Username must not exceed ${VALIDATION.USERNAME.MAX_LENGTH} characters`;
        }
        if (!VALIDATION.USERNAME.PATTERN.test(value)) {
          return VALIDATION.USERNAME.MESSAGE;
        }
        return '';

      case 'email':
        if (!value) return 'Email is required';
        if (!VALIDATION.EMAIL.PATTERN.test(value)) {
          return VALIDATION.EMAIL.MESSAGE;
        }
        return '';

      case 'password':
        if (!isEditMode && !value) return 'Password is required';
        if (value && value.length < VALIDATION.PASSWORD.MIN_LENGTH) {
          return `Password must be at least ${VALIDATION.PASSWORD.MIN_LENGTH} characters`;
        }
        if (value && !VALIDATION.PASSWORD.PATTERN.test(value)) {
          return VALIDATION.PASSWORD.MESSAGE;
        }
        return '';

      case 'confirmPassword':
        if (!isEditMode && !value) return 'Please confirm password';
        if (value && value !== formData.password) {
          return 'Passwords do not match';
        }
        return '';

      case 'fullName':
        if (!value) return 'Full name is required';
        if (value.length < VALIDATION.FULL_NAME.MIN_LENGTH) {
          return `Full name must be at least ${VALIDATION.FULL_NAME.MIN_LENGTH} characters`;
        }
        if (value.length > VALIDATION.FULL_NAME.MAX_LENGTH) {
          return `Full name must not exceed ${VALIDATION.FULL_NAME.MAX_LENGTH} characters`;
        }
        return '';

      case 'phone':
        if (value && !VALIDATION.PHONE.PATTERN.test(value)) {
          return VALIDATION.PHONE.MESSAGE;
        }
        return '';

      case 'role':
        if (!value) return 'Role is required';
        return '';

      default:
        return '';
    }
  };

  // Check username availability
  const checkUsernameAvailability = async (username) => {
    if (!username || username.length < VALIDATION.USERNAME.MIN_LENGTH) return;

    setValidating(prev => ({ ...prev, username: true }));
    try {
      const isAvailable = await userService.isUsernameAvailable(username, isEditMode ? id : null);
      if (!isAvailable) {
        setErrors(prev => ({
          ...prev,
          username: 'Username is already taken'
        }));
      }
    } catch (error) {
      console.error('Error checking username:', error);
    } finally {
      setValidating(prev => ({ ...prev, username: false }));
    }
  };

  // Check email availability
  const checkEmailAvailability = async (email) => {
    if (!email || !VALIDATION.EMAIL.PATTERN.test(email)) return;

    setValidating(prev => ({ ...prev, email: true }));
    try {
      const isAvailable = await userService.isEmailAvailable(email, isEditMode ? id : null);
      if (!isAvailable) {
        setErrors(prev => ({
          ...prev,
          email: 'Email is already taken'
        }));
      }
    } catch (error) {
      console.error('Error checking email:', error);
    } finally {
      setValidating(prev => ({ ...prev, email: false }));
    }
  };

  // Handle blur - validate and check availability
  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    
    if (error) {
      setErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }

    // Check availability for username and email
    if (name === 'username' && !error) {
      checkUsernameAvailability(value);
    } else if (name === 'email' && !error) {
      checkEmailAvailability(value);
    }
  };

  // Validate entire form
  const validateForm = () => {
    const newErrors = {};

    // Required fields
    const requiredFields = ['username', 'email', 'fullName', 'role'];
    if (!isEditMode) {
      requiredFields.push('password', 'confirmPassword');
    }

    requiredFields.forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
      }
    });

    // Optional phone validation
    if (formData.phone) {
      const phoneError = validateField('phone', formData.phone);
      if (phoneError) {
        newErrors.phone = phoneError;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    try {
      setSubmitting(true);

      const submitData = {
        username: formData.username,
        email: formData.email,
        fullName: formData.fullName,
        phone: formData.phone || null,
        role: formData.role,
        status: formData.status,
        canAccessWeb: formData.canAccessWeb,
        canAccessMobile: formData.canAccessMobile,
      };

      // Include password only if provided
      if (formData.password) {
        submitData.password = formData.password;
      }

      if (isEditMode) {
        await userService.updateUser(id, submitData);
        toast.success('User updated successfully!');
      } else {
        await userService.createUser(submitData);
        toast.success('User created successfully!');
      }

      navigate('/usermanagement');
    } catch (error) {
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(isEditMode ? 'Failed to update user' : 'Failed to create user');
      }
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? Unsaved changes will be lost.')) {
      navigate('/usermanagement');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Loader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading user data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/usermanagement')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to User List
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditMode ? 'Edit User' : 'Create New User'}
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          {isEditMode 
            ? 'Update user information and permissions' 
            : 'Add a new user to the system'}
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Username */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Username <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isEditMode} // Username cannot be changed
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.username ? 'border-red-500' : 'border-gray-300'
                  } ${isEditMode ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  placeholder="johndoe"
                />
                {validating.username && (
                  <Loader className="w-5 h-5 text-blue-600 animate-spin absolute right-3 top-1/2 transform -translate-y-1/2" />
                )}
                {!validating.username && formData.username && !errors.username && (
                  <CheckCircle className="w-5 h-5 text-green-600 absolute right-3 top-1/2 transform -translate-y-1/2" />
                )}
              </div>
              {errors.username && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.username}
                </p>
              )}
              {isEditMode && (
                <p className="mt-1 text-xs text-gray-500">Username cannot be changed</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="john@example.com"
                />
                {validating.email && (
                  <Loader className="w-5 h-5 text-blue-600 animate-spin absolute right-3 top-1/2 transform -translate-y-1/2" />
                )}
                {!validating.email && formData.email && !errors.email && (
                  <CheckCircle className="w-5 h-5 text-green-600 absolute right-3 top-1/2 transform -translate-y-1/2" />
                )}
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.email}
                </p>
              )}
            </div>

            {/* Full Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.fullName ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="John Doe"
              />
              {errors.fullName && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.fullName}
                </p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number
              </label>
              <input
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.phone ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="+62 812 3456 7890"
              />
              {errors.phone && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.phone}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Password Section - Only for Create or if changing password */}
        {!isEditMode && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Password</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.password ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.password}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Min 8 characters with uppercase, lowercase, and number
                </p>
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Confirm password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.confirmPassword}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Edit mode - Password note */}
        {isEditMode && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-900">Password Management</p>
                <p className="text-sm text-blue-700 mt-1">
                  To change the password for this user, use the "Reset Password" action in the user list.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Role & Status */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Role & Status</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Role */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role <span className="text-red-500">*</span>
              </label>
              <select
                name="role"
                value={formData.role}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.role ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                {Object.entries(ROLE_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
              {errors.role && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.role}
                </p>
              )}
            </div>

            {/* Status */}
            <HasPermission permission={PERMISSIONS.USER_UPDATE}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status <span className="text-red-500">*</span>
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {Object.entries(STATUS_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
            </HasPermission>
          </div>
        </div>

        {/* Access Control */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Access Control</h2>
          
          <div className="space-y-4">
            {/* Can Access Web */}
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  type="checkbox"
                  name="canAccessWeb"
                  checked={formData.canAccessWeb}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
              </div>
              <div className="ml-3">
                <label className="text-sm font-medium text-gray-700">
                  Can Access Web Application
                </label>
                <p className="text-sm text-gray-500">
                  Allow this user to access the web application via browser
                </p>
              </div>
            </div>

            {/* Can Access Mobile */}
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  type="checkbox"
                  name="canAccessMobile"
                  checked={formData.canAccessMobile}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
              </div>
              <div className="ml-3">
                <label className="text-sm font-medium text-gray-700">
                  Can Access Mobile Application
                </label>
                <p className="text-sm text-gray-500">
                  Allow this user to access the mobile application
                </p>
                {isFieldRole(formData.role) && (
                  <p className="text-xs text-blue-600 mt-1">
                    Note: Field workers (Supervisor/Mandor) typically require mobile access
                  </p>
                )}
              </div>
            </div>

            {/* Warning if no access selected */}
            {!formData.canAccessWeb && !formData.canAccessMobile && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-yellow-800">
                  Warning: User has no access to either web or mobile application. They will not be able to login.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 bg-gray-50 px-6 py-4 rounded-lg">
          <button
            type="button"
            onClick={handleCancel}
            disabled={submitting}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X className="w-5 h-5" />
            Cancel
          </button>
          
          <button
            type="submit"
            disabled={submitting || validating.username || validating.email}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                {isEditMode ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                {isEditMode ? 'Update User' : 'Create User'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UserForm;